import { useEffect } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ChevronLeft, MessageCircle } from 'lucide-react'
import { Link } from 'react-router-dom'
import WebApp from '@twa-dev/sdk'
import { api, AutoReplySettings, MeData, TelegramConnectionInfo } from '../api/client'
import { ErrorCard } from '../components/ui/ErrorCard'
import { Toggle } from '../components/ui/Toggle'
import { RowSkeleton } from '../components/ui/Skeleton'
import { useTelegram } from '../hooks/useTelegram'

const BOT_URL = 'https://t.me/DeloAppBot'

/**
 * Все настройки, связанные с подключёнными чатами Telegram, на одном экране.
 *
 * Раньше они жили внутри карточки на экране интеграций: автоответчик,
 * расшифровка голосовых и слежение за удалёнными были спрятаны за скроллом
 * там, где человек ищет «подключить почту». Найти их было нельзя, а кнопка
 * «Настроить» в чате с ботом вела на текст-инструкцию, где настроек не было
 * вовсе.
 */

/** Строка настройки: заголовок, пояснение и тумблер. */
function SettingRow({
  title,
  hint,
  checked,
  onChange,
  disabled,
  locked,
  lockedHint,
}: {
  title: string
  hint: string
  checked: boolean
  onChange: () => void
  disabled?: boolean
  locked?: boolean
  lockedHint?: string
}) {
  return (
    <div className="p-4 flex items-start justify-between">
      <div className="flex-1 pr-3">
        <p className={`text-base ${locked ? 'text-ink-2' : 'text-ink'}`}>{title}</p>
        <p className="text-xs text-ink-2 mt-0.5">{locked ? lockedHint ?? hint : hint}</p>
      </div>
      <Toggle
        checked={!locked && checked}
        disabled={disabled || locked}
        label={title}
        onChange={onChange}
      />
    </div>
  )
}

/** Выбор одного значения из двух — тем же приёмом, что и в профиле. */
function Choice<T extends string>({
  value,
  options,
  onSelect,
  disabled,
}: {
  value: T
  options: { value: T; label: string; hint: string }[]
  onSelect: (v: T) => void
  disabled?: boolean
}) {
  return (
    <div className="px-4 pb-4 space-y-2">
      {options.map(opt => (
        <button
          key={opt.value}
          onClick={() => value !== opt.value && onSelect(opt.value)}
          disabled={disabled}
          className={`btn-active w-full text-left px-3 py-2.5 rounded-lg border ${
            value === opt.value
              ? 'border-accent bg-accent-dim'
              : 'border-line-1 bg-surface-2'
          }`}
        >
          <span className="flex items-center gap-2">
            <span
              className={`w-3.5 h-3.5 rounded-full border-2 flex items-center justify-center shrink-0 ${
                value === opt.value ? 'border-accent' : 'border-ink-3'
              }`}
            >
              {value === opt.value && (
                <span className="w-1.5 h-1.5 rounded-full bg-accent" />
              )}
            </span>
            <span className="text-sm text-ink">{opt.label}</span>
          </span>
          <span className="block text-xs text-ink-2 mt-1 pl-5.5">{opt.hint}</span>
        </button>
      ))}
    </div>
  )
}

export function TelegramChats() {
  const qc = useQueryClient()
  const { haptic } = useTelegram()

  const { data: me } = useQuery<MeData>({
    queryKey: ['me'],
    queryFn: () => api.get('/api/me'),
    staleTime: 60_000,
  })

  const { data: tgConn, isLoading } = useQuery<TelegramConnectionInfo | null>({
    queryKey: ['telegram-connection'],
    queryFn: () =>
      api.get<TelegramConnectionInfo | null>('/api/telegram/connection').catch(() => null),
    staleTime: 30_000,
  })

  const { data: settings, error, refetch } = useQuery<AutoReplySettings | null>({
    queryKey: ['auto-reply'],
    queryFn: () => api.get<AutoReplySettings>('/api/telegram/auto-reply').catch(() => null),
    enabled: !!tgConn?.is_enabled,
    staleTime: 30_000,
  })

  const patch = useMutation({
    mutationFn: (
      body: Partial<
        Pick<
          AutoReplySettings,
          | 'enabled'
          | 'mode'
          | 'style'
          | 'voice_transcript'
          | 'incoming_voice_transcript'
          | 'deleted_capture'
        >
      >,
    ) => api.patch('/api/telegram/auto-reply', body),
    onSuccess: () => refetch(),
  })

  const patchChat = useMutation({
    mutationFn: ({ chatId, enabled }: { chatId: number; enabled: boolean }) =>
      api.patch(`/api/telegram/auto-reply/chats/${chatId}`, { auto_reply: enabled }),
    onSuccess: () => refetch(),
  })

  // Подключение делается в чате с ботом: человек сворачивает мини-апп,
  // выбирает чаты и возвращается. На возврате перечитываем статус.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return
      qc.invalidateQueries({ queryKey: ['telegram-connection'] })
      qc.invalidateQueries({ queryKey: ['auto-reply'] })
    }
    document.addEventListener('visibilitychange', onVisible)
    window.addEventListener('focus', onVisible)
    return () => {
      document.removeEventListener('visibilitychange', onVisible)
      window.removeEventListener('focus', onVisible)
    }
  }, [qc])

  const tier = me?.subscription.tier ?? 'free'
  const isPro = tier === 'pro'
  const connected = !!tgConn?.is_enabled

  // Сколько чатов выбрано в Telegram, платформа не сообщает — поэтому говорим
  // о том, что знаем: из скольких чатов уже приходили сообщения. Ноль до
  // первого сообщения — правда, а не сбой, и подпись это объясняет.
  const seen = tgConn?.chats_count ?? 0
  const seenChats =
    seen === 0
      ? 'Чаты появятся, как в них придёт сообщение'
      : `Знаю ${seen} ${seen === 1 ? 'чат' : seen < 5 ? 'чата' : 'чатов'}`

  const open = (start: string) => {
    haptic.impactOccurred('light')
    WebApp.openTelegramLink(`${BOT_URL}?start=${start}`)
  }

  const toggle = (
    body: Partial<
      Pick<
        AutoReplySettings,
        'enabled' | 'voice_transcript' | 'incoming_voice_transcript' | 'deleted_capture'
      >
    >,
  ) => {
    haptic.impactOccurred('light')
    patch.mutate(body)
  }

  return (
    <div className="flex flex-col min-h-full page-enter">
      <div className="sticky top-0 sticky-header z-10 px-4 pt-5 pb-3">
        <Link
          to="/settings"
          className="btn-active inline-flex items-center gap-1 text-sm text-ink-1 mb-2"
        >
          <ChevronLeft size={16} /> Настройки
        </Link>
        <p className="text-h1 text-ink">Чаты Telegram</p>
      </div>

      <div className="flex-1 px-4 pb-6 space-y-5">
        {isLoading && (
          <div className="space-y-2">
            {Array.from({ length: 3 }).map((_, i) => <RowSkeleton key={i} />)}
          </div>
        )}

        {error && <ErrorCard onRetry={refetch} />}

        {!isLoading && (
          <>
            {/* ── Статус подключения ── */}
            <section>
              <div className="bg-surface-1 border border-line-1 rounded-xl">
                <div className="p-4 flex items-center gap-3">
                  <div className="relative shrink-0">
                    <div
                      className={`w-9 h-9 rounded-md flex items-center justify-center ${
                        connected ? 'bg-accent-dim text-accent' : 'bg-surface-2 text-ink-2'
                      }`}
                    >
                      <MessageCircle size={18} />
                    </div>
                    {connected && (
                      <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-success border-2 border-surface-1" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-base font-medium text-ink">
                      {connected ? 'Подключено' : 'Не подключено'}
                    </p>
                    <p className="text-sm text-ink-2">
                      {connected ? seenChats : 'Требуется Telegram Premium и тариф Pro'}
                    </p>
                  </div>
                </div>
                <div className="border-t border-line-1 px-4 py-3 flex gap-2">
                  {/* Кнопки «Изменить список чатов» здесь нет намеренно: список
                      правится на стороне Telegram, ссылки туда не существует, а
                      прежняя кнопка уводила в бота — и он объяснял уже
                      подключённому, как подключиться. Шаги ниже, текстом. */}
                  {!connected && (
                    <button
                      onClick={() => open('connect_telegram')}
                      className="btn-active flex-1 text-xs font-medium text-accent bg-accent-dim px-2.5 py-1.5 rounded-md text-center"
                    >
                      Подключить
                    </button>
                  )}
                  <button
                    onClick={() => open('import_telegram')}
                    className="btn-active flex-1 text-xs font-medium text-ink-1 bg-surface-2 px-2.5 py-1.5 rounded-md text-center"
                  >
                    Загрузить архив чатов
                  </button>
                </div>
              </div>
            </section>

            <section>
              <p className="section-label">
                {connected ? 'Как изменить список чатов' : 'Как подключить'}
              </p>
              <div className="bg-surface-1 border border-line-1 rounded-xl p-4">
                <ol className="text-sm text-ink-2 space-y-1.5 list-decimal list-inside">
                  <li>Telegram → Настройки → Telegram для бизнеса</li>
                  <li>Чат-боты{connected ? ' → @DeloAppBot' : ' → добавить бота'}</li>
                  {!connected && <li>Введи @DeloAppBot</li>}
                  <li>
                    {connected
                      ? 'Управление чатами — отметь, какие мне видно'
                      : 'Выбери чаты, которые мне видно'}
                  </li>
                </ol>
                {connected && (
                  <p className="text-xs text-ink-3 mt-3">
                    Список хранится на стороне Telegram — отсюда я его не вижу
                    и поменять не могу.
                  </p>
                )}
              </div>
            </section>

            {connected && settings && (
              <>
                {/* ── Что я делаю в чатах ── */}
                <section>
                  <p className="section-label">Что я делаю в чатах</p>
                  <div className="bg-surface-1 border border-line-1 rounded-xl divide-y divide-line-1">
                    <SettingRow
                      title="Расшифровывать мои голосовые"
                      hint="Отправил голосовое или кружок — следом уйдёт его текст. Видит собеседник"
                      checked={settings.voice_transcript}
                      disabled={patch.isPending}
                      onChange={() => toggle({ voice_transcript: !settings.voice_transcript })}
                    />
                    <SettingRow
                      title="Расшифровывать чужие голосовые"
                      hint="Прислали голосовое или кружок — отвечу в чат его текстом. Прочитаешь, не включая звук"
                      checked={settings.incoming_voice_transcript}
                      disabled={patch.isPending}
                      onChange={() =>
                        toggle({ incoming_voice_transcript: !settings.incoming_voice_transcript })
                      }
                    />
                    <SettingRow
                      title="Удалённые и изменённые"
                      hint="Пришлю в личку, что удалили или переписали в чате. Копии храню 30 дней"
                      lockedHint="Доступно на Pro"
                      locked={!isPro}
                      checked={settings.deleted_capture}
                      disabled={patch.isPending}
                      onChange={() => toggle({ deleted_capture: !settings.deleted_capture })}
                    />
                    <SettingRow
                      title="Автоответчик"
                      hint="Отвечаю собеседнику за тебя, когда ты занят"
                      lockedHint="Доступно на Pro"
                      locked={!isPro}
                      checked={settings.enabled}
                      disabled={patch.isPending}
                      onChange={() => toggle({ enabled: !settings.enabled })}
                    />
                  </div>
                </section>

                {/* ── Настройки автоответчика ── */}
                {isPro && settings.enabled && (
                  <>
                    <section>
                      <p className="section-label">Как отвечать</p>
                      <div className="bg-surface-1 border border-line-1 rounded-xl pt-4">
                        <p className="px-4 text-xs text-ink-2 mb-2">Режим</p>
                        <Choice
                          value={settings.mode}
                          disabled={patch.isPending}
                          onSelect={mode => patch.mutate({ mode })}
                          options={[
                            {
                              value: 'approval',
                              label: 'С подтверждением',
                              hint: 'Покажу черновик, отправлю по кнопке',
                            },
                            {
                              value: 'auto',
                              label: 'Сразу',
                              hint: 'Отвечаю без спроса — быстро, но вслепую',
                            },
                          ]}
                        />
                        <p className="px-4 text-xs text-ink-2 mb-2">От чьего лица</p>
                        <Choice
                          value={settings.style}
                          disabled={patch.isPending}
                          onSelect={style => patch.mutate({ style })}
                          options={[
                            {
                              value: 'first_person',
                              label: 'От тебя',
                              hint: 'Собеседник не поймёт, что отвечал не ты',
                            },
                            {
                              value: 'assistant',
                              label: 'От ассистента',
                              hint: 'Честно: «на связи ассистент Delo»',
                            },
                          ]}
                        />
                      </div>
                    </section>

                    <section>
                      <p className="section-label">В каких чатах отвечать</p>
                      <div className="bg-surface-1 border border-line-1 rounded-xl divide-y divide-line-1">
                        {settings.chats.length === 0 && (
                          <p className="p-4 text-sm text-ink-2">
                            Чатов пока не видел. Появятся, как придёт первое сообщение.
                          </p>
                        )}
                        {settings.chats.map(c => (
                          <div key={c.chat_id} className="p-4 flex items-center justify-between">
                            <p className="flex-1 pr-3 text-sm text-ink truncate">
                              {c.title ?? `Чат ${c.chat_id}`}
                            </p>
                            <Toggle
                              checked={c.auto_reply}
                              disabled={patchChat.isPending}
                              label={c.title ?? 'чат'}
                              onChange={() => {
                                haptic.impactOccurred('light')
                                patchChat.mutate({
                                  chatId: c.chat_id,
                                  enabled: !c.auto_reply,
                                })
                              }}
                            />
                          </div>
                        ))}
                      </div>
                    </section>
                  </>
                )}

                {/* ── Что вижу и чего не вижу ── */}
                <section>
                  <p className="section-label">Что мне видно</p>
                  <div className="bg-surface-1 border border-line-1 rounded-xl p-4 space-y-2">
                    <p className="text-sm text-ink-2">
                      Вижу только новые сообщения в выбранных чатах. Историю до
                      подключения и невыбранные чаты — нет.
                    </p>
                    <p className="text-sm text-ink-2">
                      Чтобы отключить: Telegram → Настройки → Telegram для бизнеса →
                      Чат-боты, убрать @DeloAppBot. Перестану читать чаты сразу, а
                      сохранённые копии сообщений удалятся вместе с подключением.
                    </p>
                  </div>
                </section>
              </>
            )}
          </>
        )}
      </div>
    </div>
  )
}
